import { externalLaunchContextAtom } from '@/atoms/externalLaunchAuthAtoms';
import { sessionListHost } from '@/composition/hosts/session/session-list/session-list-host';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import { cn } from '@/lib/cn';
import { ResizableHandle, ResizablePanel } from '@heidi/design-system/resizable';
import { useAtomValue } from 'jotai';

import { useResizableSideBars } from '@/hooks/useResizableSideBars';

import { ChatRenderer } from '@/components/chat/Chat';
import { SessionAskHeidiPane } from '@/components/ui/session-ask-heidi/session-ask-heidi-pane';
import { useSessionAskHeidiChatId } from '@/components/ui/session-ask-heidi/use-session-ask-heidi-chat-id';
import { useIsNewSessionListEnabled } from '@/features/flags';
import {
  IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/features/flags/legacy-flags';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { skipLinkTargetProps } from '@heidi/design-system/skip-link';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { SessionsBarContent } from '@heidi/session';

/**
 * SessionsBar Component
 * Handles the resizable SessionsBar panel rendering
 */
export const ResizableMenuPanels = () => {
  const isConsolidatedSessionsEnabled = IS_CONSOLIDATED_SESSIONS_LIST_ENABLED();
  const isFhirAuth = useIsFhirAuth();
  const isGenericEhrLaunch = Boolean(
    useAtomValue(externalLaunchContextAtom)?.ehrProvider
  );
  const isEhrLaunch = isFhirAuth || isGenericEhrLaunch;

  const {
    activeTab,
    sidebarPanelRef,
    handleResize,
    panelConfig,
    isDragging,
    handleDragging,
  } = useResizableSideBars({
    sessionsMinWidthPx: isConsolidatedSessionsEnabled ? 300 : 220,
    askHeidiMinWidthPx: 440,
  });

  const sessionId = useCurrentSessionId();
  // Auto-select most recent chat when switching sessions, or use user's selection
  const sidebarChatId = useSessionAskHeidiChatId(sessionId);

  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const { isInSessionEvidenceShown } = useEvidenceAccess();

  // Only show the resizable panel when there's an active tab.
  // When new SidebarPanel is enabled, sessions tab is handled there, so only show for askheidi.
  const shouldShow = isSidebarPanelEnabled
    ? activeTab === 'askheidi'
    : activeTab !== 'none';

  return (
    <>
      <ResizablePanel
        ref={sidebarPanelRef}
        id="sidebar-panel"
        order={0}
        defaultSize={shouldShow ? panelConfig.defaultSize : 0}
        minSize={shouldShow ? panelConfig.minSize : 0}
        maxSize={shouldShow ? panelConfig.maxSize : 0}
        // With no handle, react-resizable-panels still makes this edge a drag
        // target, showing a resize cursor that moves nothing.
        disabled={!shouldShow}
        collapsible={activeTab !== 'askheidi'}
        collapsedSize={0}
        onResize={handleResize}
        className={cn(
          'flex h-full max-h-full max-w-4xl flex-row overflow-hidden',

          // Only apply animations when not dragging
          !isDragging && 'duration-500 ease-out',
          // Sessions CSS min-width ensures the panel doesn't render at tiny
          // intermediate sizes during the open/close animation (collapsible).
          // Not needed for askheidi since it's non-collapsible (hard-stops at minSize).
          !isSidebarPanelEnabled &&
            activeTab === 'sessions' &&
            (isConsolidatedSessionsEnabled ? 'min-w-[300px]' : 'min-w-[220px]')
        )}
      >
        {/* Sessions Panel - hidden when new SidebarPanel or new session list is enabled or in any EHR launch (Epic FHIR / generic EHR like Healthdirect) */}
        {!isSidebarPanelEnabled && !isNewSessionListEnabled && !isEhrLaunch && (
          <aside
            {...skipLinkTargetProps(
              'sidebar-session-list-panel',
              cn(
                'hidden h-full flex-col gap-3 overflow-hidden overflow-y-auto py-4 transition-all duration-500 ease-out sm:flex',
                isConsolidatedSessionsEnabled
                  ? 'gap-3 border-border bg-surface-accent'
                  : 'gap-2',
                activeTab === 'sessions'
                  ? isConsolidatedSessionsEnabled
                    ? 'w-full min-w-[300px] p-2'
                    : 'w-full min-w-[220px] px-2'
                  : 'w-0 min-w-0 border-r-0 px-0'
              )
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
            // ARD-1977: this aside carries the panel's width transition; the
            // global frame sampler reports its open/close under this name.
            data-frame-surface="sidebar_panel"
            data-testid="sidebar-session-list-panel"
          >
            <div
              className={cn(
                'flex h-full shrink-0 flex-col transition-transform duration-500 ease-out',
                isConsolidatedSessionsEnabled ? 'w-full' : 'min-w-[200px]'
              )}
            >
              <SessionsBarContent host={sessionListHost} />
            </div>
          </aside>
        )}

        {/* Ask Heidi Panel (Evidence) - unmount when closed so stream continues in Jotai and resumes on re-open */}
        {isInSessionEvidenceShown && sessionId && activeTab === 'askheidi' && (
          <aside
            className="hidden size-full flex-col gap-2 overflow-hidden sm:flex"
            onClick={(e) => {
              e.stopPropagation();
            }}
            data-testid="sidebar-ask-heidi-panel"
          >
            <div className="relative size-full overflow-hidden">
              <SessionAskHeidiPane
                sessionId={sessionId}
                sidebarChatId={sidebarChatId}
                renderEvidenceChat={(chatProps) => (
                  <ChatRenderer
                    {...chatProps}
                    sessionId={sessionId}
                    enableOnboardingTooltip={false}
                    enableSourcesSpotlight
                    analyticsSurface="side_panel"
                  />
                )}
              />
            </div>
          </aside>
        )}
      </ResizablePanel>

      {shouldShow && (
        <ResizableHandle
          withHandle={isConsolidatedSessionsEnabled}
          onDragging={handleDragging}
          className={cn(
            'transition-colors duration-150 ease-out hover:bg-border-hover',
            isConsolidatedSessionsEnabled
              ? 'group cursor-col-resize [&>div]:opacity-0 [&>div]:transition-opacity [&>div]:hover:opacity-100'
              : ''
          )}
        />
      )}
    </>
  );
};
